import { DateRange, Person } from '@mui/icons-material';
import { Box, Button, Card, Divider, Grid2, styled, Typography } from '@mui/material';
import { OperationalFaultIcon, TechnicalFaultIcon } from 'assets/custom_icons/customIcons';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { FaultCategory, type FaultData } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';
import { composeVisitFaultName } from '../../utils';
import { ImageViewer } from '../Dialogs';
import { FaultStatus } from '../Faults';

const AvaniStyledCard = styled(Card)(
  () => `
  border-radius: 16px;
  padding: 16px;

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0;
    line-height: 1;
    padding-bottom: 12px;
  }
`
);

const StyledButton = styled(Button)(
  () => `
    background-color: ${AvaniLightTheme.colors.avani.turc};
    border-radius: 8px;
    border: none;
    color: white;
    padding: 8px 12px;

    &:hover {
      border: none;
      color: ${AvaniLightTheme.colors.avani.darkTitleText};

      .MuiSvgIcon-root {
        color: ${AvaniLightTheme.colors.avani.darkTitleText};
      }
    }
`
);

export const AvaniFaultCardPDF: FC<FaultData> = (fault) => {
  const { category, isCritical, isResolved, technicianName, faultInteractions, isPendingRevision } = fault;
  const theme = AvaniLightTheme;

  const { startedAt, description, actionRecommendation, medias } = faultInteractions[0];

  // Limit Fault Images to 7 for PDF view
  const pdfMedia = medias?.slice(0, 7);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', width: '100%', alignContent: 'end' }}>
        <div style={{ flexGrow: 1 }}></div>
        <Card
          sx={{
            display: 'inline-flex',
            padding: '5px 9px 0 9px',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            marginRight: 2,
            backgroundColor: !isCritical ? theme.colors.avani.turc : theme.colors.avani.magenta
          }}
          elevation={0}
        >
          <Typography variant={'body1'} sx={{ color: 'white' }}>
            {isCritical ? 'Critical' : 'Non Critical'}
          </Typography>
        </Card>
      </Box>
      <AvaniStyledCard
        raised={true}
        sx={{
          height: '100%',
          backgroundColor: !isResolved
            ? isPendingRevision
              ? theme.colors.avani.aYellow
              : theme.colors.avani.lightMagenta
            : theme.colors.avani.lightTurc
        }}
      >
        <Grid2 container columns={6} gap={0} alignItems={'center'}>
          <Grid2 size={{ xs: 6 }} sx={{ display: 'flex', mb: 2, height: 70 }}>
            <Typography
              variant={'h5'}
              width={'100%'}
              component={'h5'}
              fontSize={18}
              fontWeight={'bold'}
              sx={{ color: theme.colors.avani.teal, flexGrow: 1, cursor: 'pointer', [`:hover`]: { opacity: 0.6 } }}
              title={composeVisitFaultName(fault)}
              noWrap
            >
              {composeVisitFaultName(fault)}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2 }}>
            <StyledButton variant="outlined">
              {<DateRange sx={{ mr: 1 }} />}
              {DateTime.fromISO(startedAt).toFormat('dd/MM/yyyy')}
            </StyledButton>
          </Grid2>
          <Grid2 size={{ xs: 2 }} sx={{ padding: '0 8px 8px 8px' }}>
            <div style={{ height: '24px', textAlign: 'center' }}>
              {category === FaultCategory.Operational
                ? OperationalFaultIcon({ width: 32, height: 32, isPDF: true })
                : TechnicalFaultIcon({ width: 32, height: 32, isPDF: true })}
            </div>
            <Typography
              fontSize={16}
              lineHeight={1}
              variant={'body1'}
              sx={{ color: theme.colors.avani.turc, textAlign: 'center' }}
            >
              {category}
            </Typography>
          </Grid2>
          <Grid2
            size={{ xs: 2 }}
            sx={{
              display: 'grid',
              minHeight: '70px',
              alignContent: 'center',
              justifyItems: 'end',
              [`& .MuiBox-root > .MuiBox-root`]: { display: 'inline!important' },
              [`& span.MuiTypography-h4`]: { display: 'inline!important', paddingLeft: '5px' }
            }}
          >
            <FaultStatus visitFault={fault} />
          </Grid2>
          <Grid2 size={{ xs: 6 }} sx={{ py: 2 }}>
            <Divider sx={{ borderBottom: `1px dashed ${theme.colors.avani.turc}`, backgroundColor: 'transparent' }} />
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Typography fontWeight={'bold'} fontSize={18} sx={{ color: theme.colors.avani.teal }}>
              Description
            </Typography>
            <Typography fontSize={14} variant={'body2'} minHeight={'59px'}>
              {description ?? ''}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Typography fontWeight={'bold'} fontSize={18} sx={{ color: theme.colors.avani.teal, mt: 2 }}>
              Recommendation
            </Typography>
            <Typography fontSize={14} variant={'body2'} sx={{ minHeight: '59px' }}>
              {actionRecommendation ?? ''}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 6 }} sx={{ py: 2 }}>
            <Divider sx={{ borderBottom: `1px dashed ${theme.colors.avani.turc}`, backgroundColor: 'transparent' }} />
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Typography fontWeight={'bold'} fontSize={18} sx={{ color: theme.colors.avani.teal }}>
              Technician
            </Typography>
            <Typography fontWeight={'bold'} fontSize={16}>
              <Person sx={{ display: 'inline', float: 'left', color: theme.colors.avani.turc }} />
              {technicianName}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 6 }} sx={{ py: 2 }}>
            <Divider sx={{ borderBottom: `1px dashed ${theme.colors.avani.turc}`, backgroundColor: 'transparent' }} />
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Typography fontWeight={'bold'} fontSize={18} sx={{ color: theme.colors.avani.teal }}>
              Interaction Photos
            </Typography>
            <Grid2 container sx={{ marginTop: 2, justifyContent: 'flex-start', gap: 2 }}>
              {pdfMedia?.map(({ contentUrl }, i) => {
                return (
                  <Grid2 key={i}>
                    <ImageViewer
                      key={contentUrl}
                      index={i}
                      imageSrc={medias ?? []}
                      avatarWidth={180}
                      avatarHeight={180}
                      avatarSx={{ borderRadius: 1 }}
                      isPDF
                    />
                  </Grid2>
                );
              })}
            </Grid2>
          </Grid2>
        </Grid2>
      </AvaniStyledCard>
    </Box>
  );
};
